export type ExampleLanguage = "el" | "en";

interface LocalizedText {
    el: string;
    en: string;
}

export interface ExampleCategory {
    id: string;
    title: LocalizedText;
    shortTitle: LocalizedText;
    icon: string;
    description: LocalizedText;
    disclaimer?: LocalizedText;
    examples: LocalizedText[];
}

export const EXAMPLE_UI_COPY = {
    assistantTitle: {
        el: "Public Sales Assistant",
        en: "Public Sales Assistant"
    },
    panelSubtitle: {
        el: "Δες τι μπορείς να ζητήσεις και πάτησε σε οποιοδήποτε παράδειγμα για να ξεκινήσεις.",
        en: "See what you can ask and click any example to get started."
    },
    viewExamples: {
        el: "Δες παραδείγματα",
        en: "View examples"
    },
    hideExamples: {
        el: "Απόκρυψη παραδειγμάτων",
        en: "Hide examples"
    },
    languageLabel: {
        el: "Γλώσσα παραδειγμάτων",
        en: "Examples language"
    },
    inputPlaceholder: {
        el: "Πώς μπορώ να βοηθήσω σήμερα;",
        en: "How can I help you today?"
    },
    replyPlaceholder: {
        el: "Απάντησε στον Sales Assistant...",
        en: "Reply to Sales Assistant..."
    },
    excludedTitle: {
        el: "⚠️ Προϊόντα εκτός καταλόγου",
        en: "⚠️ Outside product scope"
    },
    excludedDescription: {
        el: "Περιορισμένη λειτουργικότητα για Marketplace.",
        en: "Limited functionality for Marketplace."
    },
    excludedDescription2: {
        el: "Για αυτές τις κατηγορίες δεν υποστηρίζεται αναζήτηση με βάση περιγραφή ή ανάγκη πελάτη.",
        en: "For these categories, search by description or customer need is not supported."
    },
    excludedItems: {
        el: ["Marketplace"],
        en: ["Marketplace"]
    }
} as const;

export const EXAMPLE_CATEGORIES: ExampleCategory[] = [
    {
        id: "specs",
        title: {
            el: "1. Έλεγχος χαρακτηριστικών",
            en: "1. Check product specs"
        },
        shortTitle: { el: "Χαρακτηριστικά", en: "Specs" },
        icon: "🔍",
        description: {
            el: "Βρες τεχνικά χαρακτηριστικά, διαστάσεις, υποστηριζόμενες λειτουργίες και πληροφορίες συμβατότητας για ένα προϊόν.",
            en: "Find technical specs, dimensions, supported features, and compatibility details for a product."
        },
        examples: [
            {
                el: "Ένας πελάτης αναφέρει ότι το Xiaomi 17 έχει κάμερα 100MP ενώ εμείς αναφέρουμε στο public.gr 50MP. Μπορείς να το εξακριβώσεις online;",
                en: "A customer says the Xiaomi 17 has a 100MP camera, while our website says 50MP. Can you verify it online?"
            },
            {
                el: "το βιβλίο 2041424 ειναι κατάλληλο για 8 ετων;",
                en: "Is book 2041424 suitable for 8-year-olds?"
            },
            {
                el: "Το 1966528 υποστηρίζει δύο SIM κάρτες;",
                en: "Does 1966528 support dual SIM?"
            },
            {
                el: "Το Samsung Galaxy S25 υποστηρίζει δύο SIM κάρτες;",
                en: "Does the Samsung Galaxy S25 support dual SIM?"
            },
            {
                el: "Τι διαστάσεις έχει το WNT-EPQ25525M;",
                en: "What are the dimensions of WNT-EPQ25525M?"
            },
            {
                el: "Βρες μου αν το BOSCH WQB245B8GR είναι συμβατό με Home Connect.",
                en: "Check whether BOSCH WQB245B8GR is compatible with Home Connect."
            },
            {
                el: "Το iPhone 17 έχει οθόνη AMODEL;",
                en: "Does the iPhone 17 have an AMOLED display?"
            }
        ]
    },
    {
        id: "compare",
        title: {
            el: "2. Συγκρίσεις προϊόντων",
            en: "2. Compare products"
        },
        shortTitle: { el: "Συγκρίσεις", en: "Compare" },
        icon: "⚖️",
        description: {
            el: "Σύγκρινε δύο ή περισσότερα προϊόντα σε επιδόσεις, κατανάλωση, βασικές διαφορές και συνολική αξία αγοράς.",
            en: "Compare two or more products on performance, consumption, key differences, and overall value."
        },
        examples: [
            {
                el: "Ποιο κινητό βγάζει καλύτερες selfie από τα 2072209, 1831996 και 1892112;",
                en: "Which phone takes better selfies among 2072209, 1831996, and 1892112?"
            },
            {
                el: "Ποιο ψυγείο έχει χαμηλότερη κατανάλωση, το LG GMG960EVEE ή το Bosch KFN96AXEA;",
                en: "Which fridge has lower energy consumption, the LG GMG960EVEE or the Bosch KFN96AXEA?"
            },
            {
                el: "Ποιο έχει καλύτερη ευκρίνεια στην οθόνη μεταξύ iPhone 17 pro και Samsung S26;",
                en: "Which has better screen clarity between the iPhone 17 pro and the Samsung S26?"
            },
            {
                el: "Ποια είναι η διαφορά μεταξύ του 1994735 και του 1530290;",
                en: "What is the difference between 1994735 and 1530290?"
            },
            {
                el: "Τι διαφορές έχουν το Samsung Galaxy S25 και το Galaxy S25 Ultra;",
                en: "What are the differences between the Samsung Galaxy S25 and the Galaxy S25 Ultra?"
            },
            {
                el: "Ποιο θεωρείς best value for money από τα 2023635 και 1912745;",
                en: "Which one is better value for money between 2023635 and 1912745?"
            }
        ]
    },
    {
        id: "recommend",
        title: {
            el: "3. Προτάσεις προϊόντων",
            en: "3. Get recommendations"
        },
        shortTitle: { el: "Προτάσεις", en: "Suggest" },
        icon: "💡",
        description: {
            el: "Περιέγραψε ανάγκη, budget, brand ή χρήση και πάρε προτάσεις που ταιριάζουν στο σενάριο του πελάτη.",
            en: "Describe the need, budget, brand, or use case and get recommendations that fit the customer scenario."
        },
        examples: [
            {
                el: 'Ο πελάτης αναφέρει ότι του αρέσει το βιβλίο "Η μεγάλη χίμαιρα". Τι του προτείνουμε;',
                en: 'The customer says they like the book "The Great Chimera". What would we recommend?'
            },
            {
                el: "Ψάχνουμε για κινητό, βοήθησε με να βρούμε τι χρειάζεται ο πελάτης.",
                en: "We're looking for a phone, help me figure out what the customer needs."
            },
            {
                el: "Ψάχνω laptop, αλλά το αίτημα του πελάτη είναι πολύ γενικό.",
                en: "I'm looking for a laptop, but the customer's request is too broad."
            },
            {
                el: "Θέλω μία τηλεόραση για gaming μέχρι 1500 ευρώ.",
                en: "I need a TV for gaming up to 1500 euros."
            },
            {
                el: "Βρες μου κλιματιστικό για δωμάτιο περίπου 15 τ.μ. με τιμή μέχρι 600 ευρώ.",
                en: "Find me an air conditioner for a room around 15 sq.m. with a budget up to 600 euros."
            },
            {
                el: "Θέλω μια τοστιέρα μέχρι 80 ευρώ αλλά να μπορεί να ψήνει και κρέας.",
                en: "I want a sandwich maker up to 80 euros, but it should also handle grilling a steak."
            },
            {
                el: "Βρες μου μια μηχανή εσπρέσο με ενσωματωμένο μύλο, ατμό και γρήγορη θέρμανση.",
                en: "Find me an espresso machine with a built-in grinder, steam wand, and fast heat-up time."
            },
            {
                el: "Βρες μου ένα φούρνο μικροκυμάτων που να έχει και air fryer.",
                en: "Find me a microwave oven that also has an air fryer function."
            },
            {
                el: "Ψάχνω laptop 15 ιντσών για gaming χωρίς να ξεπεράσω τα 1500 ευρώ.",
                en: "I'm looking for a 15-inch gaming laptop under 1500 euros."
            }
        ]
    },
    {
        id: "compatibility",
        title: {
            el: "4. Έλεγχος συμβατότητας",
            en: "4. Check compatibility"
        },
        shortTitle: { el: "Συμβατότητα", en: "Compatible" },
        icon: "🔗",
        description: {
            el: "Δες αν δύο προϊόντα συνεργάζονται σωστά ή αν μπορούν να συνδεθούν και να χρησιμοποιηθούν μαζί.",
            en: "Check whether two products work together properly or can be connected and used together."
        },
        examples: [
            {
                el: "Είναι συμβατά τα 2072209 και 1850816;",
                en: "Are 2072209 and 1850816 compatible?"
            },
            {
                el: "Κάνε έλεγχο συμβατότητας για Samsung Galaxy S25 και Galaxy Watch 7.",
                en: "Run a compatibility check for Samsung Galaxy S25 and Galaxy Watch 7."
            },
            {
                el: "Μπορώ να συνδέσω το 1951163 με το 2072209;",
                en: "Can I connect 1951163 with 2072209?"
            },
            {
                el: "Είναι συμβατό το BOSCH WQB245B8GR με το Home Connect;",
                en: "Is BOSCH WQB245B8GR compatible with Home Connect?"
            }
        ]
    },
    {
        id: "accessories",
        title: {
            el: "5. Αξεσουάρ προϊόντων",
            en: "5. Product accessories"
        },
        shortTitle: { el: "Αξεσουάρ", en: "Accessories" },
        icon: "🧩",
        description: {
            el: "Βρες σχετικά αξεσουάρ, αναλώσιμα ή συμπληρωματικά προϊόντα που ταιριάζουν με μια βασική συσκευή ή μοντέλο.",
            en: "Find relevant accessories, consumables, or companion products that match a main device or model."
        },
        examples: [
            {
                el: "Τι αξεσουάρ προτείνεις για το Samsung Galaxy S25 Titanium Black;",
                en: "What accessories do you recommend for the Samsung Galaxy S25 Titanium Black?"
            },
            {
                el: "Βρες μου θήκη και tempered glass για iPhone 17.",
                en: "Find me a case and tempered glass for the iPhone 17."
            },
            {
                el: "Βρες μου καθαριστικά για τη Delonghi Magnifica Evo;",
                en: "Find me cleaning products for the Delonghi Magnifica Evo."
            },
            {
                el: "Βρες μου αναλώσιμα για HP M207dw.",
                en: "Find me consumables for the HP M207dw."
            },
            {
                el: "Ποια αξεσουάρ ταιριάζουν με το 2072209;",
                en: "Which accessories fit 2072209?"
            }
        ]
    },
    {
        id: "services",
        title: {
            el: "6. Υπηρεσίες προϊόντων",
            en: "6. Product services"
        },
        shortTitle: { el: "Υπηρεσίες", en: "Services" },
        icon: "🛠️",
        description: {
            el: "Δες ποιες διαθέσιμες υπηρεσίες μπορούν να συνοδεύσουν μια κατηγορία ή ένα συγκεκριμένο προϊόν.",
            en: "Check which available services can accompany a product category or a specific product."
        },
        examples: [
            {
                el: "Τι υπηρεσίες έχουμε για laptops;",
                en: "What services do we offer for laptops?"
            },
            {
                el: "Υπάρχουν διαθέσιμες υπηρεσίες για τηλεοράσεις;",
                en: "Are there available services for TVs?"
            },
            {
                el: "Τι υπηρεσίες υπάρχουν για το 2072209;",
                en: "Which services are available for 2072209?"
            },
            {
                el: "Τι υπηρεσίες μπορώ να προτείνω μαζί με το Samsung Galaxy S25;",
                en: "Which services can I suggest with the Samsung Galaxy S25?"
            },
            {
                el: "Έχω πελάτη που αγοράζει το BOSCH WQB245B8GR. Τι υπηρεσίες υπάρχουν;",
                en: "A customer is buying the BOSCH WQB245B8GR. Which services are available?"
            },
            {
                el: "Τι υπηρεσίες υπάρχουν για το κλιματιστικό 1955050;",
                en: "Which services are available for air conditioner 1955050?"
            }
        ]
    },
    {
        id: "alternatives",
        title: {
            el: "7. Εναλλακτικές επιλογές",
            en: "7. Find alternatives"
        },
        shortTitle: { el: "Εναλλακτικά", en: "Alternatives" },
        icon: "🔄",
        description: {
            el: "Ζήτα αντίστοιχα προϊόντα με βάση brand, τιμή, διαθεσιμότητα ή παρόμοια χαρακτηριστικά.",
            en: "Ask for similar products based on brand, price, availability, or comparable features."
        },
        examples: [
            {
                el: "Ο πελάτης βρήκε στον Κωτσόβολο τη Delonghi La Specialista Arte EC9155. Τι έχουμε να του προτείνουμε;",
                en: "The customer found the Delonghi La Specialista Arte EC9155 at Kotsovolos. What alternatives can we suggest from public.gr?"
            },
            {
                el: "Ο πελάτης ενδιαφέρεται για το Samsung DV90DG6845LB από το skroutz. Τι έχουμε να του προτείνουμε;",
                en: "The customer is interested in the Samsung DV90DG6845LB from Skroutz. What alternatives can we suggest from public.gr?"
            },
            {
                el: "Βρες μου κάτι αντίστοιχο με το Samsung S26 512GB αλλά σε Xiaomi.",
                en: "Find me something similar to the Samsung S26 512GB but from Xiaomi."
            },
            {
                el: "Βρες μου ένα αντίστοιχο του BOSCH WQB245B8GR αλλά σε LG.",
                en: "Find me an LG alternative to BOSCH WQB245B8GR."
            },
            {
                el: "Βρες μου κάποιο αντίστοιχο με το 2000605 αλλά λίγο φθηνότερο.",
                en: "Find me an alternative to 2000605 that is slightly cheaper."
            },
            {
                el: "Θέλω κάτι σαν το iPhone 16 αλλά σε Samsung.",
                en: "I want something like the iPhone 16 but from Samsung."
            },
            {
                el: "Θέλω κάτι αντίστοιχο με το Dyson V15 αλλά πιο οικονομικό.",
                en: "I want something similar to the Dyson V15 but more affordable."
            }
        ]
    },
    {
        id: "availability",
        title: {
            el: "8. Διαθεσιμότητα και υπόσχεση παράδοσης",
            en: "8. Availability and delivery promise"
        },
        shortTitle: { el: "Διαθεσιμότητα", en: "Availability" },
        icon: "📦",
        description: {
            el: "Έλεγξε αν ένα συγκεκριμένο προϊόν είναι διαθέσιμο ή αναζήτησε προϊόντα που υπάρχουν σε συγκεκριμένο κατάστημα, πόλη ή περιοχή.",
            en: "Check whether a specific product is available or search for products available in a specific store, city, or area."
        },
        disclaimer: {
            el: "Μπορείς να ζητήσεις είτε έλεγχο stock για συγκεκριμένο τίτλο/SKU είτε αναζήτηση προϊόντων με φίλτρο καταστήματος ή περιοχής.",
            en: "You can ask either for a stock check on a specific title/SKU or for product search filtered by store or area."
        },
        examples: [
            {
                el: "Υπάρχει διαθέσιμο το 2072209;",
                en: "Is 2072209 currently available?"
            },
            {
                el: "Τι απόθεμα έχουμε στο Γέρακα για το 2000605;",
                en: "What inventory do we have in Gerakas for 2000605?"
            },
            {
                el: "Βρες μου laptops μέχρι 1000 ευρώ που είναι διαθέσιμα στο Public Golden Hall.",
                en: "Find laptops up to 1000 euros available at Public Golden Hall."
            },
            {
                el: "Πρότεινέ μου κινητά μέχρι 300 ευρώ που είναι διαθέσιμα σε καταστήματα της Αθήνας.",
                en: "Suggest phones up to 300 euros that are available in Athens stores."
            },
            {
                el: "Βρες μου ακουστικά κατάλληλα για τρέξιμο διαθέσιμα στα βόρεια προάστια.",
                en: "Find headphones suitable for running available in the northern suburbs."
            },
            {
                el: "Πες μου διαθεσιμότητα και χρόνο παράδοσης για το Samsung Galaxy S25 Ultra Titanium Black.",
                en: "Tell me the availability and delivery time for the Samsung Galaxy S25 Ultra Titanium Black."
            },
            {
                el: "Βρες μου σε ποιο κατάστημα στα βόρεια προάστια είναι ετοιμοπαράδοτο το Galaxy S25 Titanium Black.",
                en: "Find which store in the northern suburbs has the Galaxy S25 Titanium Black available for immediate pickup."
            }
        ]
    },
    {
        id: "orders",
        title: {
            el: "9. Παραγγελίες",
            en: "9. Orders"
        },
        shortTitle: { el: "Παραγγελίες", en: "Orders" },
        icon: "🧾",
        description: {
            el: "Αναζήτησε παραγγελίες, δες ανοιχτές παραγγελίες, έλεγξε ποια προϊόντα περιείχαν και πότε ή πού παραλήφθηκαν.",
            en: "Search for orders, find open orders, check which products they included, and see when or where they were received."
        },
        examples: [
            {
                el: "Να ψάξουμε για μια παραγγελία;",
                en: "Can we look up an order?"
            },
            {
                el: "Τι προϊόντα είχε η παραγγελία R352812768;",
                en: "Which products were included in order R352812768?"
            },
            {
                el: "Πότε παρελήφθη η παραγγελία R352812768 και από πού;",
                en: "When was order R352812768 received, and from where?"
            },
            {
                el: "Βρες το προϊόν που είχε η παραγγελία R316729337 και πες μου σε ποιο κατάστημα έχουμε απόθεμα στα νότια προάστια.",
                en: "Find the product included in order R316729337 and tell me which store in the southern suburbs has it in stock."
            }
        ]
    }
];

export const getLocalizedText = (text: LocalizedText, language: ExampleLanguage): string => text[language];
